import React, { ReactElement } from 'react';

import Header from '~/components/titles/Header';
import Subheader from '~/components/titles/Subheader';
import Divider from '~/components/flow/Divider';
import Link from '~/components/flow/Link';
import Break from '~/components/flow/Break';
import Gap from '~/components/flow/Gap';
import { FONT_WEIGHTS } from '~/constants/typography';
import urls from '~/urls';

import auriUrls from '../../urls';
import Container from '../../components/Container';

function About(): ReactElement {
  // Styling
  const headerStyle = {
    margin: 0,
    fontWeight: FONT_WEIGHTS.light,
  };
  const subheaderStyle = {
    fontWeight: FONT_WEIGHTS.thin,
  };
  const boldStyle = {
    fontWeight: FONT_WEIGHTS.bold,
  };

  return (
    <Container>
      <Header style={headerStyle}>
        What&apos;s most important in preventing theft?
      </Header>
      <Divider />
      <Subheader style={subheaderStyle}>
        <b style={boldStyle}>Know who&apos;s stealing from you.</b>
      </Subheader>
      <Gap />
      <Subheader style={subheaderStyle}>
        Auri is an app that lets you encode images you want to privately share
        with identifying messages.
      </Subheader>
      <Gap />
      <Subheader style={subheaderStyle}>
        Instead of distributing your original image, send an encoded image Auri
        creates for you. If someone leaks that encoded image, you can decode it
        with Auri to retrieve the identifying message, revealing who the pirate
        was.
      </Subheader>
      <Break />
      <Subheader style={subheaderStyle}>
        <b style={boldStyle}>Who&apos;s this service intended for?</b>
      </Subheader>
      <Gap />
      <Subheader style={subheaderStyle}>
        I originally built Auri with creators who want to privately and directly
        share images with multiple identified people in mind. For example, if
        you have a Patreon and know specifically who you&apos;re sharing to,
        Auri can be a good fit.
      </Subheader>
      <Gap />
      <Subheader style={subheaderStyle}>
        In theory, you could encode images sent to a single person with
        sensitive info to threaten mutal damage from leaking, but I ask you
        don&apos;t do so without informing them. The app doesn&apos;t aim to
        prevent miscrediting publicly shared images or leaks from private groups
        where sharing isn&apos;t direct.
      </Subheader>
      <Break />
      <Subheader style={subheaderStyle}>
        <b style={boldStyle}>Are there any disclaimers?</b>
      </Subheader>
      <Gap />
      <Subheader style={subheaderStyle}>
        If not obvious, the protocol Auri uses isn&apos;t foolproof so please
        don&apos;t rely on it absolutely. I recommend treating it like a
        forensic tool that gives your content an extra layer of protection to
        deter rather than solve piracy.
      </Subheader>
      <Gap />
      <Subheader style={subheaderStyle}>
        That said, I did try to make the protocol as robust as I could. In many
        cases, you can manually realign edits of encoded images with your
        original image if decoding doesn&apos;t work initially. The idea is
        breaking the protocol is annoying without making things unpalatable.
        Creators also have the option to release original images after a leak to
        make decoding public. I mean, if I was a pirate, I wouldn&apos;t want to
        leak anything that had even the slightest chance of revealing my full
        name and address.
      </Subheader>
      <Break />
      <Subheader style={subheaderStyle}>
        <b style={boldStyle}>How does the protocol work?</b>
      </Subheader>
      <Gap />
      <Subheader style={subheaderStyle}>
        The core concept is no <i>natural</i> interpretation <i>function</i> is{' '}
        <i>injective</i>. This quality of how statements and propositions relate
        allows encoding and decoding through subtle syntax manipulation without
        affecting semantics.
      </Subheader>
      <Break />
      <Subheader style={subheaderStyle}>
        <b style={boldStyle}>Will you scale Auri?</b>
      </Subheader>
      <Gap />
      <Subheader style={subheaderStyle}>
        There are no current plans to scale Auri. The bottleneck is GPU cost.
        Even if my code is conscious of span, several features are impractical
        unless actually run with parallelism.
      </Subheader>
      <Gap />
      <Subheader style={subheaderStyle}>
        If you&apos;d like API access for something commercial, you can contact
        me on my <Link href={urls.discordInvite}>Discord</Link>. Also don&apos;t
        hesitate to get in touch if you&apos;re a developer who wants to try
        extending the core concept to other mediums like text or music.
      </Subheader>
      <Break />
      <Subheader>
        Launched in July of 2025 by{' '}
        <Link href={auriUrls.kindredYouTube}>Kindred</Link> as proof of concept
        for an idea I had in the shower.
      </Subheader>
    </Container>
  );
}

export default About;
